import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "@/lib/constants";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Loader2, Send, Clock, AlertTriangle, CheckCircle2,
  Eye, Paintbrush, Globe, RefreshCw, Calendar as CalendarIcon, User,
  ArrowUpRight, Megaphone, Layers, FileText, Palette,
  Zap, Repeat, Sparkles, LayoutGrid, PenLine,
  Building2, UserCircle, Lightbulb, Settings, Mail,
  Copy, Check, Trash2, ChevronDown, ChevronUp, X,
} from "lucide-react";
import { WeekPlanner } from "@/components/WeekPlanner";
import { ContentCalendar } from "@/components/ContentCalendar";
import ContentProductionBoard from "./ContentProductionBoard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const STATUS_CONFIG = {
  detected: { label: "Pending", color: "bg-yellow-100 text-yellow-700 border-yellow-200", dot: "bg-yellow-400" },
  queued: { label: "Queued", color: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-400" },
  brief_ready: { label: "Brief Ready", color: "bg-indigo-100 text-indigo-700 border-indigo-200", dot: "bg-indigo-400" },
  sent_to_designer: { label: "With Designer", color: "bg-purple-100 text-purple-700 border-purple-200", dot: "bg-purple-400" },
  in_revision: { label: "In Revision", color: "bg-orange-100 text-orange-700 border-orange-200", dot: "bg-orange-400" },
  ready_to_post: { label: "Ready to Post", color: "bg-green-100 text-green-700 border-green-200", dot: "bg-green-400" },
  posted: { label: "Posted", color: "bg-slate-100 text-slate-500 border-slate-200", dot: "bg-slate-400" },
  draft: { label: "Draft", color: "bg-sky-100 text-sky-700 border-sky-200", dot: "bg-sky-400" },
};

const MARKET_COLORS = {
  nigeria: "bg-emerald-50 text-emerald-700 border-emerald-200",
  africa: "bg-amber-50 text-amber-700 border-amber-200",
  uk: "bg-sky-50 text-sky-700 border-sky-200",
  global: "bg-slate-50 text-slate-600 border-slate-200",
};

const RHYTHM_COLORS = {
  monday_anchor: "bg-blue-600 text-white",
  wednesday_anchor: "bg-violet-600 text-white",
  friday_anchor: "bg-teal-600 text-white",
  friday_proof: "bg-teal-600 text-white",
  reactive: "bg-orange-500 text-white",
  evergreen: "bg-emerald-600 text-white",
  cultural_moment: "bg-rose-500 text-white",
  conversion: "bg-amber-600 text-white",
};

const RHYTHM_LABELS = {
  monday_anchor: "Mon Anchor",
  wednesday_anchor: "Wed Anchor",
  friday_anchor: "Fri Proof",
  friday_proof: "Fri Proof",
  reactive: "Reactive",
  evergreen: "Evergreen",
  cultural_moment: "Cultural",
  conversion: "Conversion",
};

// ─── Generate Post Section ────────────────────────────────────

function GeneratePostSection() {
  const [postType, setPostType] = useState("company");
  const [topic, setTopic] = useState("");
  const [angle, setAngle] = useState("");
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [topicBank, setTopicBank] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingDesigner, setSendingDesigner] = useState(false);
  const [copied, setCopied] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState("");
  const [refining, setRefining] = useState(false);
  const [showDesignerDialog, setShowDesignerDialog] = useState(false);
  const [designerDeadline, setDesignerDeadline] = useState("");
  const [showConfig, setShowConfig] = useState(false);
  const [designerEmail, setDesignerEmail] = useState("");
  const [designerName, setDesignerName] = useState("");
  const [designerConfigured, setDesignerConfigured] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(true);
  const [showTopicBank, setShowTopicBank] = useState(false);
  const [schedulingTopic, setSchedulingTopic] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const draftEditorRef = useCallback(node => {
    if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [draft]);

  useEffect(() => {
    fetchDesignerConfig();
    fetchDrafts();
    fetchTopicBank();
    autoSuggestUpcoming();
  }, []);

  // Auto-load draft from URL param (e.g., ?draft=draft_abc123)
  const [searchParams, setSearchParams] = useSearchParams();
  const autogenFired = useRef(false);
  useEffect(() => {
    const draftId = searchParams.get("draft");
    if (draftId && drafts.length > 0) {
      const target = drafts.find(d => d.draft_id === draftId);
      if (target) {
        setDraft(target);
        setEditContent(target.content || "");
        setShowDrafts(true);
      }
    }
  }, [searchParams, drafts]);

  // Prefill + optionally auto-generate when arriving from the Weekly BD Playbook
  // (e.g. /content-studio?topic=...&angle=...&post_type=company&autogen=1)
  useEffect(() => {
    const t = searchParams.get("topic");
    const a = searchParams.get("angle");
    const pt = searchParams.get("post_type");
    if (t) setTopic(t);
    if (a) setAngle(a);
    if (pt === "personal" || pt === "company") setPostType(pt);
    if (t && searchParams.get("autogen") === "1" && !autogenFired.current) {
      autogenFired.current = true;
      generatePost({ topic: t, angle: a || "", post_type: (pt === "personal" || pt === "company") ? pt : "company" });
      // Drop autogen from the URL so a refresh/Back doesn't re-fire a paid LLM generation.
      const next = new URLSearchParams(searchParams);
      next.delete("autogen");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDesignerConfig = async () => {
    try {
      const res = await fetch(`${API}/content-gen/config/designer`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setDesignerEmail(data.designer_email || "");
        setDesignerName(data.designer_name || "");
        setDesignerConfigured(data.configured);
      }
    } catch (e) { /* ignore */ }
  };

  const fetchTopicBank = async () => {
    try {
      const res = await fetch(`${API}/content-gen/topic-bank`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTopicBank(data.topics || []);
      }
    } catch (e) { /* ignore */ }
  };

  const autoSuggestUpcoming = async () => {
    try {
      const res = await fetch(`${API}/content-gen/auto-suggest-upcoming`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.auto_suggested > 0) {
          toast.info(`Rex auto-suggested ${data.auto_suggested} topic${data.auto_suggested > 1 ? "s" : ""} for upcoming dates`);
          fetchTopicBank();
        }
      }
    } catch (e) { /* ignore */ }
  };

  const fetchDrafts = async () => {
    try {
      const res = await fetch(`${API}/content-gen/drafts?limit=10`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setDrafts(data.drafts || []);
      }
    } catch (e) { /* ignore */ }
  };

  const suggestTopics = async () => {
    setLoadingTopics(true);
    try {
      const res = await fetch(`${API}/content-gen/suggest-topics`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSuggestedTopics(data.topics || []);
        // Refresh topic bank since suggestions are now persisted
        fetchTopicBank();
        toast.success(`${data.topics?.length || 0} topics saved to your Topic Bank`);
      }
    } catch (e) {
      toast.error("Failed to load topic suggestions");
    } finally {
      setLoadingTopics(false);
    }
  };

  const generatePost = async (arg) => {
    const ov = (arg && typeof arg.topic === "string") ? arg : null;
    const useTopic = (ov ? ov.topic : topic).trim();
    const useAngle = (ov ? (ov.angle || "") : angle).trim();
    const usePostType = ov ? (ov.post_type || "company") : postType;
    if (!useTopic) {
      toast.error("Please enter or select a topic");
      return;
    }
    setGenerating(true);
    setDraft(null);
    try {
      // Step 1: kick off async generation — returns draft_id immediately with status="generating"
      const res = await fetch(`${API}/content-gen/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ topic: useTopic, angle: useAngle || null, post_type: usePostType }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Generation failed");
        setGenerating(false);
        return;
      }
      const initial = await res.json();
      setDraft(initial);
      // Show the placeholder so the user sees something is happening
      toast.message("Generating, this can take 20-40 seconds...");
      fetchDrafts();

      // Step 2: poll until status leaves 'generating' (max 120s)
      const startedAt = Date.now();
      const maxWaitMs = 120000;
      let polls = 0;
      while (Date.now() - startedAt < maxWaitMs) {
        polls += 1;
        await new Promise(r => setTimeout(r, 2500));
        try {
          const sRes = await fetch(`${API}/content-gen/drafts/${initial.draft_id}/status`, {
            credentials: "include",
          });
          if (!sRes.ok) continue;
          const sData = await sRes.json();
          if (sData.status === "draft") {
            setDraft(sData);
            setEditContent(sData.content || "");
            toast.success(`Draft generated in ${Math.round((Date.now() - startedAt) / 1000)}s`);
            fetchDrafts();
            setGenerating(false);
            return;
          }
          if (sData.status === "generation_failed") {
            toast.error(sData.error_message || "Generation failed — please try again");
            setDraft(null);
            setGenerating(false);
            return;
          }
        } catch (_) { /* keep polling */ }
        // Don't spam — log every 10s to console
        if (polls % 4 === 0) {
          // eslint-disable-next-line no-console
          console.log(`[generate] still waiting after ${Math.round((Date.now()-startedAt)/1000)}s`);
        }
      }
      // Fallthrough — timed out client-side. Backend may still finish; surface gently.
      toast.warning("Still working in the background. Check Drafts list shortly.");
      fetchDrafts();
    } catch (e) {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/content-gen/drafts/${draft.draft_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: editContent }),
      });
      if (res.ok) {
        const updated = await res.json();
        setDraft(updated);
        if (updated.user_amended) {
          toast.success("Saved — Rex will learn from your edits on future posts", { duration: 4000 });
        } else {
          toast.success("Draft saved");
        }
        fetchDrafts();
      }
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendToDesigner = async () => {
    if (!draft) return;
    if (!designerConfigured) {
      setShowConfig(true);
      toast.error("Configure designer email first");
      return;
    }
    // Show date picker dialog
    const defaultDate = new Date(Date.now() + 2 * 86400000).toISOString().split("T")[0];
    setDesignerDeadline(defaultDate);
    setShowDesignerDialog(true);
  };

  const confirmSendToDesigner = async () => {
    if (!draft) return;
    // Save first if content changed
    if (editContent !== draft.content) {
      await saveDraft();
    }
    setSendingDesigner(true);
    setShowDesignerDialog(false);
    try {
      const res = await fetch(`${API}/content-gen/drafts/${draft.draft_id}/send-to-designer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ completion_date: designerDeadline }),
      });
      if (res.ok) {
        const result = await res.json();
        const msg = result.task_created
          ? `Pushed to designer! Task created for ${designerDeadline} deadline.`
          : (result.message || "Pushed to pipeline & designer!");
        toast.success(msg);
        setDraft({ ...draft, sent_to_designer: true, status: "with_designer" });
        fetchDrafts();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Send failed");
      }
    } catch (e) {
      toast.error("Send failed");
    } finally {
      setSendingDesigner(false);
    }
  };

  const saveDesignerConfig = async () => {
    if (!designerEmail.trim() || !designerEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSavingConfig(true);
    try {
      const res = await fetch(`${API}/content-gen/config/designer`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ designer_email: designerEmail.trim(), designer_name: designerName.trim() }),
      });
      if (res.ok) {
        setDesignerConfigured(true);
        setShowConfig(false);
        toast.success("Designer email saved");
      }
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSavingConfig(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(editContent || draft?.content || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const refineDraft = async () => {
    if (!draft || !refinePrompt.trim()) return;
    setRefining(true);
    try {
      const res = await fetch(`${API}/content-gen/drafts/${draft.draft_id}/refine`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ instruction: refinePrompt.trim(), current_content: editContent }),
      });
      if (res.ok) {
        const updated = await res.json();
        setDraft(updated);
        setEditContent(updated.content);
        setRefinePrompt("");
        toast.success("Rex refined the post based on your instruction");
        fetchDrafts();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Refinement failed");
      }
    } catch (e) {
      toast.error("Refinement failed");
    } finally {
      setRefining(false);
    }
  };

  const loadDraft = (d) => {
    setDraft(d);
    setEditContent(d.content);
    setTopic(d.topic);
    setAngle(d.angle || "");
    setPostType(d.post_type);
    setShowDrafts(false);
  };

  const deleteDraft = async (draftId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this draft? This cannot be undone.")) return;
    try {
      await fetch(`${API}/content-gen/drafts/${draftId}`, { method: "DELETE", credentials: "include" });
      fetchDrafts();
      if (draft?.draft_id === draftId) {
        setDraft(null);
        setEditContent("");
      }
      toast.success("Draft deleted");
    } catch (e) { /* ignore */ }
  };

  const deleteTopic = async (topicId) => {
    try {
      await fetch(`${API}/content-gen/topic-bank/${topicId}`, { method: "DELETE", credentials: "include" });
      fetchTopicBank();
      toast.success("Topic removed");
    } catch (e) { toast.error("Failed to delete topic"); }
  };

  const scheduleTopic = async (topicId) => {
    if (!scheduleDate) { toast.error("Pick a date"); return; }
    try {
      const res = await fetch(`${API}/content-gen/topic-bank/${topicId}/schedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ scheduled_date: scheduleDate }),
      });
      if (res.ok) {
        toast.success("Topic scheduled to calendar");
        setSchedulingTopic(null);
        setScheduleDate("");
        fetchTopicBank();
      } else {
        toast.error("Failed to schedule");
      }
    } catch (e) { toast.error("Failed to schedule"); }
  };

  const selectTopicFromBank = (t) => {
    setTopic(t.title);
    setAngle(t.angle || "");
    if (t.post_type) setPostType(t.post_type);
    setShowTopicBank(false);
    toast.success("Topic loaded — ready to generate");
  };

  return (
    <div className="flex flex-col h-full" data-testid="generate-post-section">
      <ScrollArea className="flex-1">
        <div className="p-6 max-w-4xl mx-auto space-y-6">
          {/* Designer Config Banner */}
          {!designerConfigured && !showConfig && (
            <button
              onClick={() => setShowConfig(true)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-sm hover:bg-amber-100 transition-colors"
              data-testid="config-designer-banner"
            >
              <Settings className="w-4 h-4" />
              <span>Configure designer email to enable "Push to Designer"</span>
              <ArrowUpRight className="w-4 h-4 ml-auto" />
            </button>
          )}

          {/* Designer Config Panel */}
          {showConfig && (
            <div className="p-4 rounded-lg border border-slate-200 bg-white space-y-3" data-testid="designer-config-panel">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" /> Designer Configuration
                </h3>
                <button onClick={() => setShowConfig(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={designerName}
                  onChange={(e) => setDesignerName(e.target.value)}
                  placeholder="Designer name"
                  data-testid="designer-name-input"
                />
                <Input
                  value={designerEmail}
                  onChange={(e) => setDesignerEmail(e.target.value)}
                  placeholder="designer@company.com"
                  data-testid="designer-email-input"
                />
              </div>
              <Button onClick={saveDesignerConfig} disabled={savingConfig} size="sm" data-testid="save-designer-email-btn">
                {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          )}

          {/* Post Type Toggle */}
          <div className="flex items-center gap-3">
            <div className="flex bg-slate-100 rounded-lg p-1" data-testid="post-type-toggle">
              <button
                onClick={() => setPostType("company")}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${postType === "company" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="post-type-company"
              >
                <Building2 className="w-4 h-4" /> Company Post
              </button>
              <button
                onClick={() => setPostType("personal")}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${postType === "personal" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="post-type-personal"
              >
                <UserCircle className="w-4 h-4" /> Personal Post
              </button>
            </div>
            <div className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => setShowDrafts(!showDrafts)} className="text-slate-500 gap-1.5" data-testid="toggle-drafts-btn">
              <FileText className="w-4 h-4" /> Drafts {(() => { const c = drafts.filter(d => (d.post_type || "company") === postType).length; return c > 0 ? `(${c})` : ""; })()}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowTopicBank(!showTopicBank)} className="text-slate-500 gap-1.5" data-testid="toggle-topic-bank-btn">
              <Lightbulb className="w-4 h-4" /> Topic Bank {(() => { const c = topicBank.filter(t => (t.post_type || "company") === postType).length; return c > 0 ? `(${c})` : ""; })()}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowConfig(!showConfig)} className="text-slate-400" data-testid="settings-btn">
              <Settings className="w-4 h-4" />
            </Button>
          </div>

          {/* Saved Drafts List — filtered by active post type silo */}
          {showDrafts && (() => {
            const filteredDrafts = drafts.filter(d => (d.post_type || "company") === postType);
            if (filteredDrafts.length === 0) {
              return (
                <div className="border border-slate-200 rounded-lg bg-white px-4 py-6 text-center text-xs text-slate-400" data-testid="drafts-list-empty">
                  No {postType === "personal" ? "personal" : "company"} drafts yet. Generate one above to get started.
                </div>
              );
            }
            return (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white" data-testid="drafts-list">
              {filteredDrafts.map((d) => (
                <button
                  key={d.draft_id}
                  onClick={() => loadDraft(d)}
                  className="w-full flex flex-col gap-1.5 px-4 py-3 text-left hover:bg-slate-50 transition-colors group"
                  data-testid={`draft-item-${d.draft_id}`}
                >
                  <div className="flex items-center gap-3 w-full">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_CONFIG[d.status]?.dot || "bg-slate-300"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{d.topic}</p>
                      {d.assigned_to_name && <p className="text-[11px] text-purple-600 truncate" data-testid={`draft-assignee-${d.draft_id}`}>Assigned to {d.assigned_to_name}{d.source === "weekly_playbook" ? " · from Weekly BD Playbook" : ""}</p>}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 ${STATUS_CONFIG[d.status]?.color || "bg-slate-100 text-slate-500"}`}>
                      {STATUS_CONFIG[d.status]?.label || d.status}
                    </span>
                    {d.user_amended && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200 flex-shrink-0">
                        Amended
                      </span>
                    )}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${d.post_type === "personal" ? "bg-violet-50 text-violet-600 border border-violet-200" : "bg-blue-50 text-blue-600 border border-blue-200"}`}>
                      {d.post_type === "personal" ? "Personal" : "Company"}
                    </span>
                    <span className="text-xs text-slate-400 flex-shrink-0">{new Date(d.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                    <PenLine className="w-3.5 h-3.5 text-blue-400 opacity-60 group-hover:opacity-100 transition-all flex-shrink-0" />
                    <button
                      onClick={(e) => deleteDraft(d.draft_id, e)}
                      className="text-slate-400 hover:text-red-600 transition-all flex-shrink-0 p-1 rounded hover:bg-red-50"
                      data-testid={`delete-draft-${d.draft_id}`}
                      title="Delete draft"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-2 ml-5">{d.content?.slice(0, 150)}{d.content?.length > 150 ? "..." : ""}</p>
                </button>
              ))}
            </div>
            );
          })()}

          {/* Topic Bank Panel */}
          {showTopicBank && (
            <div className="border border-slate-200 rounded-lg bg-white" data-testid="topic-bank-panel">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" /> Topic Bank
                  <span className="text-xs font-normal text-slate-400">({topicBank.length} topics)</span>
                </h3>
                <button onClick={() => setShowTopicBank(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              {(() => { const filteredBank = topicBank.filter(t => (t.post_type || "company") === postType); return (
              filteredBank.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-slate-400">
                  No {postType === "personal" ? "personal" : "company"} topics yet. Click "Suggest Topics" to generate ideas.
                </div>
              ) : (
                <div className="divide-y divide-slate-50 max-h-[320px] overflow-y-auto">
                  {filteredBank.map((t) => (
                    <div key={t.topic_id} className="px-4 py-3 hover:bg-slate-50 transition-colors group" data-testid={`topic-bank-item-${t.topic_id}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800">{t.title}</p>
                          {t.angle && <p className="text-xs text-slate-500 mt-0.5">{t.angle}</p>}
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.post_type === "personal" ? "bg-violet-50 text-violet-600 border border-violet-200" : "bg-blue-50 text-blue-600 border border-blue-200"}`}>
                              {t.post_type === "personal" ? "Personal" : "Company"}
                            </span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{t.tag}</span>
                            {t.occasion && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
                                {t.occasion}
                              </span>
                            )}
                            {t.status === "scheduled" && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200 flex items-center gap-0.5">
                                <CalendarIcon className="w-2.5 h-2.5" /> {t.scheduled_date}
                              </span>
                            )}
                            <span className="text-[10px] text-slate-300">{new Date(t.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="outline" size="sm" className="text-[10px] h-7 px-2" onClick={() => selectTopicFromBank(t)} data-testid={`use-topic-${t.topic_id}`}>
                            <PenLine className="w-3 h-3 mr-1" /> Use
                          </Button>
                          {t.status !== "scheduled" && (
                            schedulingTopic === t.topic_id ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="date"
                                  value={scheduleDate}
                                  onChange={(e) => setScheduleDate(e.target.value)}
                                  className="text-[10px] h-7 px-1 border rounded"
                                  data-testid={`schedule-date-${t.topic_id}`}
                                />
                                <Button variant="outline" size="sm" className="text-[10px] h-7 px-2 text-green-600" onClick={() => scheduleTopic(t.topic_id)} data-testid={`confirm-schedule-${t.topic_id}`}>
                                  <Check className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-[10px] h-7 px-1 text-slate-400" onClick={() => setSchedulingTopic(null)}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <Button variant="outline" size="sm" className="text-[10px] h-7 px-2" onClick={() => { setSchedulingTopic(t.topic_id); setScheduleDate(""); }} data-testid={`schedule-topic-${t.topic_id}`}>
                                <CalendarIcon className="w-3 h-3 mr-1" /> Schedule
                              </Button>
                            )
                          )}
                          <button onClick={() => deleteTopic(t.topic_id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all p-1" data-testid={`delete-topic-${t.topic_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )); })()}
            </div>
          )}

          {/* Topic Input */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-slate-700">Topic</label>
              <Button variant="ghost" size="sm" onClick={suggestTopics} disabled={loadingTopics} className="text-xs text-blue-600 gap-1" data-testid="suggest-topics-btn">
                {loadingTopics ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lightbulb className="w-3 h-3" />}
                Suggest Topics
              </Button>
            </div>
            <Input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Why African boards need executive coaching now"
              className="text-sm"
              data-testid="topic-input"
            />

            {/* Suggested Topics */}
            {suggestedTopics.length > 0 && (
              <div className="grid grid-cols-2 gap-2" data-testid="suggested-topics">
                {suggestedTopics.map((t, i) => (
                  <button
                    key={i}
                    onClick={() => { setTopic(t.title); setAngle(t.angle || ""); if (t.post_type) setPostType(t.post_type); }}
                    className={`text-left p-3 rounded-lg border transition-all hover:shadow-sm ${topic === t.title ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                    data-testid={`topic-suggestion-${i}`}
                  >
                    <p className="text-sm font-medium text-slate-800">{t.title}</p>
                    <p className="text-xs text-slate-500 mt-1">{t.angle}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${t.post_type === "personal" ? "bg-violet-50 text-violet-600 border border-violet-200" : "bg-blue-50 text-blue-600 border border-blue-200"}`}>
                        {t.post_type === "personal" ? "Personal" : "Company"}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{t.tag}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Angle Input */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Angle <span className="font-normal text-slate-400">(optional)</span></label>
            <Input
              value={angle}
              onChange={(e) => setAngle(e.target.value)}
              placeholder="e.g. Contrarian take on why most coaching programs fail"
              className="text-sm"
              data-testid="angle-input"
            />
          </div>

          {/* Generate Button */}
          <Button
            onClick={generatePost}
            disabled={generating || !topic.trim()}
            className="w-full gap-2"
            size="lg"
            data-testid="generate-post-btn"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
            ) : (
              <><PenLine className="w-4 h-4" /> Generate {postType === "personal" ? "Personal" : "Company"} Post</>
            )}
          </Button>

          {/* Generated Draft */}
          {draft && (
            <div ref={draftEditorRef} className="space-y-3 border border-slate-200 rounded-xl p-4 bg-white" data-testid="draft-editor">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  Draft
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${draft.post_type === "personal" ? "bg-violet-50 text-violet-600 border border-violet-200" : "bg-blue-50 text-blue-600 border border-blue-200"}`}>
                    {draft.post_type === "personal" ? "Personal" : "Company"}
                  </span>
                  {draft.sent_to_designer && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-200 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Sent to Designer
                    </span>
                  )}
                </h3>
                <span className="text-xs text-slate-400">{editContent.length} chars</span>
              </div>

              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[280px] text-sm leading-relaxed resize-y font-sans"
                data-testid="draft-content-editor"
              />

              {draft.user_amended && (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
                  <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Rex has learned from your edits on this post. Future posts will adapt to your voice.</span>
                </div>
              )}

              {draft.knowledge_refs && draft.knowledge_refs.length > 0 && (
                <div className="px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-xs" data-testid="knowledge-attribution">
                  <div className="flex items-center gap-2 text-indigo-800 font-semibold mb-1.5">
                    <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Drew from {draft.knowledge_refs.length} past upload{draft.knowledge_refs.length > 1 ? "s" : ""}</span>
                  </div>
                  <div className="space-y-1">
                    {draft.knowledge_refs.slice(0, 4).map((ref, i) => (
                      <div key={i} className="flex items-center gap-2 text-indigo-700">
                        <FileText className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate flex-1">{ref.filename}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 flex-shrink-0">{Math.round((ref.score || 0) * 100)}%</span>
                        {ref.tags?.topic && ref.tags.topic !== "Other" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-indigo-200 flex-shrink-0">{ref.tags.topic}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {editContent !== draft.content && !draft.user_amended && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                  <PenLine className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>You've made changes. Save to teach Rex your preferred style.</span>
                </div>
              )}

              {/* Refine with Rex */}
              <div className="border border-slate-200 rounded-lg bg-slate-50/50 p-3 space-y-2" data-testid="refine-with-rex">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-semibold text-slate-700">Refine with Rex</span>
                  <span className="text-xs text-slate-400">— describe what you want changed</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={refinePrompt}
                    onChange={(e) => setRefinePrompt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && refinePrompt.trim() && !refining) { e.preventDefault(); refineDraft(); }}}
                    placeholder='e.g. "Make it shorter", "Add a CTA about governance services", "More conversational tone"'
                    className="flex-1 text-sm"
                    disabled={refining}
                    data-testid="refine-prompt-input"
                  />
                  <Button
                    size="sm"
                    onClick={refineDraft}
                    disabled={refining || !refinePrompt.trim()}
                    className="gap-1.5 bg-blue-600 hover:bg-blue-700 shrink-0"
                    data-testid="refine-btn"
                  >
                    {refining ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {refining ? "Refining..." : "Refine"}
                  </Button>
                </div>
                {draft.refinement_history?.length > 0 && (
                  <p className="text-[10px] text-slate-400">
                    Refined {draft.refinement_history.length} time{draft.refinement_history.length > 1 ? "s" : ""} — last: "{draft.refinement_history[draft.refinement_history.length - 1]?.instruction}"
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 pt-1 flex-wrap">
                <Button variant="outline" size="sm" onClick={copyToClipboard} className="gap-1.5 text-xs" data-testid="copy-draft-btn">
                  {copied ? <><Check className="w-3 h-3 text-green-500" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </Button>
                <Button variant="outline" size="sm" onClick={saveDraft} disabled={saving} className="gap-1.5 text-xs" data-testid="save-draft-btn">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Save
                </Button>
                <div className="flex-1" />
                {/* Schedule date */}
                <div className="flex items-center gap-1.5">
                  <input
                    type="date"
                    className="text-xs border border-slate-200 rounded-md px-2 py-1 h-8"
                    value={draft.scheduled_date || ""}
                    onChange={async (e) => {
                      const date = e.target.value;
                      if (!date) return;
                      try {
                        const res = await fetch(`${API}/content-gen/drafts/${draft.draft_id}/schedule`, {
                          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
                          body: JSON.stringify({ scheduled_date: date }),
                        });
                        if (res.ok) {
                          const result = await res.json();
                          setDraft(result.draft);
                          toast.success(`Scheduled for ${new Date(date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}`);
                        }
                      } catch(err) { toast.error("Failed to schedule"); }
                    }}
                    data-testid="schedule-date-input"
                  />
                </div>
                {draft.status === "with_designer" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs text-green-600 border-green-200 hover:bg-green-50"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/content-gen/drafts/${draft.draft_id}/mark-returned`, {
                          method: "POST", credentials: "include",
                        });
                        if (res.ok) { toast.success("Marked as returned from designer"); setDraft({...draft, status: "ready_to_post"}); fetchDrafts(); }
                      } catch(e) { toast.error("Failed"); }
                    }}
                    data-testid="mark-returned-btn"
                  >
                    <CheckCircle2 className="w-3 h-3" /> Designer Done
                  </Button>
                )}
                {(draft.status === "ready_to_post" || draft.status === "draft" || draft.status === "with_designer") && draft.status !== "posted" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/content-gen/drafts/${draft.draft_id}/mark-posted`, {
                          method: "POST", credentials: "include",
                        });
                        if (res.ok) { toast.success("Marked as posted!"); setDraft({...draft, status: "posted"}); fetchDrafts(); }
                      } catch(e) { toast.error("Failed"); }
                    }}
                    data-testid="mark-posted-btn"
                  >
                    <Globe className="w-3 h-3" /> Mark Posted
                  </Button>
                )}
                {draft.status !== "with_designer" && draft.status !== "ready_to_post" && draft.status !== "posted" && (
                  <Button
                    onClick={sendToDesigner}
                    disabled={sendingDesigner}
                    size="sm"
                    className="gap-1.5 text-xs bg-purple-600 hover:bg-purple-700"
                    data-testid="send-to-designer-btn"
                  >
                    {sendingDesigner ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    Push to Designer
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
      {/* Designer Deadline Dialog */}
      <Dialog open={showDesignerDialog} onOpenChange={setShowDesignerDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Push to Designer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">When do you need the design completed?</p>
            <div>
              <Label className="text-xs text-slate-500">Completion deadline</Label>
              <Input type="date" value={designerDeadline} onChange={e => setDesignerDeadline(e.target.value)} data-testid="designer-deadline-input" className="text-base"/>
            </div>
            <p className="text-xs text-slate-400">A scrum task will be created for the designer with this deadline.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDesignerDialog(false)}>Cancel</Button>
            <Button onClick={confirmSendToDesigner} disabled={!designerDeadline} data-testid="confirm-push-designer-btn" className="bg-purple-600 hover:bg-purple-700">Push to Designer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Pipeline View (Preserved from ContentStudioPanel) ────────

function PipelineView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [designerStats, setDesignerStats] = useState(null);
  const [cadence, setCadence] = useState(null);
  const [sla, setSla] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, slaRes] = await Promise.all([
        fetch(`${API}/content-gen/drafts`, { credentials: "include" }),
        fetch(`${API}/content-gen/designer-sla`, { credentials: "include" }),
      ]);

      if (qRes.ok) {
        const data = await qRes.json();
        const allDrafts = data.drafts || data || [];
        // Map draft statuses to pipeline stages
        const statusMap = {
          active: () => true,
          queued: (d) => d.status === "draft",
          designer: (d) => ["sent_to_designer", "with_designer"].includes(d.status),
          ready: (d) => d.status === "ready_to_post",
          posted: (d) => d.status === "posted",
        };
        const filterFn = statusMap[filter] || statusMap.active;
        const filtered = allDrafts.filter(filterFn);
        // Transform to pipeline card format
        setItems(filtered.map(d => ({
          action_id: d.draft_id,
          description: d.topic || d.title || "Draft",
          status: d.status,
          content_type: d.content_type || "linkedin_post",
          created_at: d.created_at,
          updated_at: d.updated_at,
          _draft: d,
        })));
        // Build designer stats from drafts
        const withDesigner = allDrafts.filter(d => ["sent_to_designer","with_designer"].includes(d.status));
        const readyToPost = allDrafts.filter(d => d.status === "ready_to_post");
        const posted30d = allDrafts.filter(d => d.status === "posted");
        setDesignerStats({
          with_designer: withDesigner.length,
          with_designer_items: withDesigner,
          ready_to_post: readyToPost.length,
          ready_to_post_items: readyToPost,
          in_revision: 0, in_revision_items: [],
          overdue: 0, overdue_items: [],
          throughput_30d: { posts_completed: posted30d.length, avg_turnaround_hours: 0, missed_deadlines: 0 },
        });
      }
      if (slaRes.ok) setSla(await slaRes.json());
    } catch (e) {
      console.error("Pipeline fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const filters = [
    { key: "active", label: "All Active" },
    { key: "queued", label: "Queued" },
    { key: "designer", label: "With Designer" },
    { key: "ready", label: "Ready to Post" },
    { key: "posted", label: "Posted" },
  ];

  return (
    <div className="flex flex-col h-full" data-testid="pipeline-view">
      {/* Stats Strip */}
      <div className="px-4 py-2 bg-slate-50/80 border-b border-slate-100 flex items-center gap-4 text-xs flex-wrap">
        {designerStats && (
          <>
            <span className="flex items-center gap-1.5 text-purple-600">
              <Paintbrush className="w-3 h-3" />
              <span className="font-medium">{designerStats.with_designer}</span> with designer
            </span>
            {designerStats.overdue > 0 && (
              <span className="flex items-center gap-1.5 text-red-600">
                <AlertTriangle className="w-3 h-3" />
                <span className="font-medium">{designerStats.overdue}</span> overdue
              </span>
            )}
            <span className="flex items-center gap-1.5 text-green-600">
              <CheckCircle2 className="w-3 h-3" />
              <span className="font-medium">{designerStats.ready_to_post}</span> ready
            </span>
          </>
        )}
        {sla && (
          <span className={`flex items-center gap-1.5 ${sla.overdue > 0 ? "text-red-600" : "text-blue-600"}`}>
            <Clock className="w-3 h-3" />
            SLA: {sla.overdue > 0 ? `${sla.overdue} overdue` : "All on track"}
            {sla.avg_turnaround_hours && <span className="text-slate-400 ml-1">({sla.avg_turnaround_hours}h avg)</span>}
          </span>
        )}
        {cadence && cadence.gaps?.length > 0 && (
          <span className="flex items-center gap-1.5 text-amber-600 ml-auto">
            <CalendarIcon className="w-3 h-3" />
            {cadence.gaps.length} cadence gap{cadence.gaps.length > 1 ? "s" : ""}
          </span>
        )}
        <button onClick={fetchQueue} className="text-slate-400 hover:text-blue-500 p-1 rounded-md hover:bg-blue-50 transition-colors ml-auto" data-testid="refresh-pipeline-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* SLA Alert Strip */}
      {sla && sla.items?.length > 0 && (
        <div className="px-4 py-2 border-b border-slate-100 space-y-1">
          {sla.items.filter(i => i.is_overdue).map(item => (
            <div key={item.draft_id} className="flex items-center gap-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              <span className="font-medium">{item.topic}</span> — {Math.round(item.hours_elapsed)}h with designer (48h SLA breached)
            </div>
          ))}
        </div>
      )}

      {/* Weekly Rhythm Strip */}
      {cadence?.anchor_plan && (
        <div className="px-4 py-2 border-b border-slate-100 flex gap-2">
          {cadence.anchor_plan.map(a => {
            const rhythmColor = a.rhythm_role ? RHYTHM_COLORS[a.rhythm_role] : "";
            return (
              <div key={a.slot} className={`flex-1 rounded-lg px-3 py-1.5 text-center text-[10px] border ${a.status === "filled" ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-slate-50 border-dashed border-slate-200 text-slate-400"}`}>
                <div className="font-semibold">{a.day}</div>
                {a.status === "filled" ? (
                  <>
                    <div className="truncate">{a.signal_preview?.slice(0, 22) || "Filled"}</div>
                    {a.rhythm_label && (
                      <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[8px] font-bold ${rhythmColor || "bg-blue-100 text-blue-600"}`}>
                        {RHYTHM_LABELS[a.rhythm_role] || a.rhythm_label}
                      </span>
                    )}
                  </>
                ) : (
                  <div className="truncate">Open</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="px-3 py-2 border-b border-slate-100 flex gap-1">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1 text-xs rounded-md transition-colors ${filter === f.key ? "bg-blue-100 text-blue-700 font-medium" : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}
            data-testid={`pipeline-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Cards Grid */}
      <ScrollArea className="flex-1">
        <div className="p-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <Paintbrush className="w-8 h-8 text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No content in this view</p>
              <p className="text-xs text-slate-300 mt-1">Switch to Generate to create new posts</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              {items.map(item => (
                <PipelineCard key={item.action_id} item={item} />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function PipelineCard({ item }) {
  const draft = item._draft || {};
  const statusKey = item.status === "sent_to_designer" ? "with_designer" : (item.status || "draft");
  const status = STATUS_CONFIG[statusKey] || STATUS_CONFIG.draft || { label: statusKey, color: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" };
  const content = draft.content || "";

  return (
    <div className="p-3.5 rounded-xl border border-slate-200 bg-white hover:border-blue-200 transition-all hover:shadow-sm" data-testid={`pipeline-card-${item.action_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot || "bg-slate-400"}`} />
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${status.color}`}>{status.label}</span>
          {draft.voice && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">{draft.voice === "company" ? "Company" : "Personal"}</span>}
        </div>
        <span className="text-[10px] text-slate-400">{draft.created_at?.substring(0, 10)}</span>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-snug mb-2 line-clamp-2">
        {item.description}
      </p>
      {content && <p className="text-xs text-slate-500 line-clamp-2 mb-2">{content.substring(0, 120)}{content.length > 120 ? "..." : ""}</p>}
      <div className="flex items-center gap-2 text-[11px] text-slate-500">
        {draft.user_amended && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200">Amended</span>}
        {item.status === "sent_to_designer" && draft.designer_sent_at && (
          <span className="flex items-center gap-1 text-purple-600">
            <Clock className="w-3 h-3" />
            With designer since {new Date(draft.designer_sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export default function ContentStudioPage({ user }) {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(() =>
    (searchParams.get("topic") || searchParams.get("autogen")) ? "generate" : "production"
  );

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="content-studio-page">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center">
            <PenLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Content Studio</h1>
            <p className="text-xs text-slate-500">Design, learn, and publish — all in one place</p>
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex bg-slate-100 rounded-lg p-1" data-testid="view-tabs">
          <button
            onClick={() => setView("production")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${view === "production" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-production"
          >
            <Layers className="w-4 h-4" /> Production
          </button>
          <button
            onClick={() => setView("generate")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${view === "generate" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-generate"
          >
            <PenLine className="w-4 h-4" /> Generate with AI
          </button>
          <button
            onClick={() => setView("planner")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${view === "planner" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-planner"
          >
            <LayoutGrid className="w-4 h-4" /> Week Planner
          </button>
          <button
            onClick={() => setView("calendar")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${view === "calendar" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-calendar"
          >
            <CalendarIcon className="w-4 h-4" /> Calendar
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {view === "production" && <div className="p-6"><ContentProductionBoard user={user} /></div>}
        {view === "generate" && <GeneratePostSection />}
        {view === "planner" && <WeekPlanner />}
        {view === "calendar" && <ContentCalendar />}
      </div>
    </div>
  );
}
